import React from 'react';
import { Plus, Trash2, GripVertical, AlertCircle, CheckCircle } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';

interface Indicator {
  id: string;
  category: string;
  name: string;
  description: string;
  input_type: string;
  min_score: number;
  max_score: number;
  weight: number;
  is_required: boolean;
  options: any[];
}

interface CategoryConfig {
  enabled: boolean;
  weight: number;
}

interface CategoryCardProps {
  category: string;
  label: string;
  config: CategoryConfig;
  indicators: Indicator[];
  onToggle: (enabled: boolean) => void;
  onWeightChange: (weight: string) => void;
  onAddIndicator: () => void;
  onUpdateIndicator: (id: string, field: string, value: any) => void;
  onDeleteIndicator: (id: string) => void;
}

export default function CategoryCard({
  category,
  label,
  config,
  indicators,
  onToggle,
  onWeightChange,
  onAddIndicator,
  onUpdateIndicator,
  onDeleteIndicator,
}: CategoryCardProps) {
  const indicatorWeightTotal = indicators.reduce((sum, ind) => sum + (ind.weight || 0), 0);
  const isIndicatorWeightValid = !config.enabled || indicatorWeightTotal === config.weight;

  return (
    <div className={`border rounded-lg bg-white ${!config.enabled ? 'opacity-50' : ''}`}>
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onToggle(!config.enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                config.enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  config.enabled ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
            <div>
              <h4 className="font-semibold">{label}</h4>
              <p className="text-sm text-gray-500">{indicators.length} indikator</p>
            </div>
          </div>

          <div className="text-right">
            <label className="block text-xs text-gray-500 mb-1">Bobot Kategori</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={config.weight}
                onChange={e => onWeightChange(e.target.value)}
                className="w-20 px-2 py-1 border rounded text-center focus:outline-none focus:border-blue-500"
                disabled={!config.enabled}
              />
              <span className="text-sm font-medium">%</span>
            </div>
          </div>
        </div>
      </div>

      {config.enabled && (
        <div className="p-4 space-y-4">
          {/* Weight Summary */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium">Total Bobot Indikator:</span>
            <div className="flex items-center gap-2">
              <span
                className={`text-lg font-bold ${isIndicatorWeightValid ? 'text-green-600' : 'text-red-600'}`}
              >
                {indicatorWeightTotal}%
              </span>
              {isIndicatorWeightValid ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
          </div>

          {/* Indicators */}
          <div className="space-y-3">
            {indicators.map(indicator => (
              <div key={indicator.id} className="border rounded-lg p-4 bg-white space-y-3">
                <div className="flex items-start gap-3">
                  <div className="mt-2 cursor-move">
                    <GripVertical className="w-5 h-5 text-gray-400" />
                  </div>

                  <div className="flex-1 space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Nama indikator"
                        value={indicator.name}
                        onChange={e => onUpdateIndicator(indicator.id, 'name', e.target.value)}
                        className="px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      <CustomSelect
                        value={indicator.input_type}
                        onChange={v => onUpdateIndicator(indicator.id, 'input_type', v)}
                        placeholder="Tipe input"
                        width="w-full"
                        options={[
                          { value: 'kualitatif', label: 'Kualitatif' },
                          { value: 'rasio', label: 'Rasio' },
                          { value: 'angka', label: 'Angka' },
                        ]}
                      />
                    </div>

                    <textarea
                      placeholder="Deskripsi indikator"
                      value={indicator.description}
                      onChange={e => onUpdateIndicator(indicator.id, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                    />

                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Min Skor</label>
                        <input
                          type="number"
                          value={indicator.min_score}
                          onChange={e =>
                            onUpdateIndicator(indicator.id, 'min_score', parseFloat(e.target.value))
                          }
                          className="w-full px-2 py-1 border rounded focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Max Skor</label>
                        <input
                          type="number"
                          value={indicator.max_score}
                          onChange={e =>
                            onUpdateIndicator(indicator.id, 'max_score', parseFloat(e.target.value))
                          }
                          className="w-full px-2 py-1 border rounded focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Bobot (%)</label>
                        <input
                          type="number"
                          value={indicator.weight}
                          onChange={e =>
                            onUpdateIndicator(indicator.id, 'weight', parseFloat(e.target.value))
                          }
                          className="w-full px-2 py-1 border rounded focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Wajib</label>
                        <button
                          type="button"
                          onClick={() =>
                            onUpdateIndicator(indicator.id, 'is_required', !indicator.is_required)
                          }
                          className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                            indicator.is_required ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                              indicator.is_required ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onDeleteIndicator(indicator.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add Indicator Button */}
          <button
            type="button"
            onClick={onAddIndicator}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-4 h-4" /> Tambah Indikator
          </button>
        </div>
      )}
    </div>
  );
}
